import axios from 'axios';

// Base API configuration
const getBaseURL = () => {
  // Detect environment based on current hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://127.0.0.1:8787/api/v1';
    }
  }
  // Production API
  return 'https://gkr-tags-api-production.scottyvw.workers.dev/api/v1';
};

export const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'gkr-tags-frontend-2024', // Use same API key as frontend
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API types
export interface Product {
  id: string;
  type: 'bag_tag' | 'keyring';
  name: string;
  description: string;
  base_price: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: string;
  user_id: string;
  order_number: string;
  status: string;
  product_type: 'bag_tag' | 'keyring';
  material: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  first_name: string;
  membership_number: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

// API functions
export const apiService = {
  // Authentication
  async login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  // Products
  async getProducts(): Promise<Product[]> {
    const response = await api.get('/products');
    return response.data.data;
  },

  async createProduct(product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) {
    const response = await api.post('/admin/products', product);
    return response.data;
  },

  async updateProduct(id: string, product: Partial<Product>) {
    const response = await api.put(`/admin/products/${id}`, product);
    return response.data;
  },

  async deleteProduct(id: string) {
    const response = await api.delete(`/admin/products/${id}`);
    return response.data;
  },

  // Orders
  async getOrders(): Promise<Order[]> {
    const response = await api.get('/admin/orders');
    return response.data.data;
  },

  async updateOrderStatus(id: string, status: string, notes?: string) {
    const response = await api.patch(`/admin/orders/${id}/status`, { status, notes });
    return response.data;
  },

  // Users/Customers
  async getUsers(): Promise<User[]> {
    const response = await api.get('/admin/users');
    return response.data.data;
  },

  // Analytics
  async getAnalytics() {
    const response = await api.get('/admin/analytics');
    return response.data;
  },

  // Health check
  async getHealth() {
    const response = await axios.get('http://127.0.0.1:8787/health');
    return response.data;
  }
};